import React from 'react'
import H from 'history'
import { BreachNotification } from './BreachNotification/BreachNotification'
import { BreachEvent, useFeature } from '@news-mono/web-common'
import { showSportBreach, showBreachDiscount } from '../../helpers'
import { SportBreachNotification } from './SportBreachNotification/SportBreachNotification'
import { BlackFridayBreachNotification } from './BlackFridayBreachNotification/BlackFridayBreachNotification'

interface BreachNotificationWrapperProps {
    breachDeal: string
    articleId: string
    breachCreativeName: string
    onEvent: (event: BreachEvent) => void
    type: BreachWallContentType
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
}

export type BreachWallContentType =
    | 'default'
    | 'sport'
    | 'black-friday'
    | '50-off-breach-discount-govan50'
    | '50-off-breach-discount-coviddig'
    | '50-off-breach-discount-printdig'

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscountGovan: boolean
    canShowBreachDiscountCovid: boolean
    canShowBlackFriday: boolean
    canShowPrintDig: boolean
}

export function useBreachWallConfig(location: H.Location) {
    return useBreachWallContentType(location, {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscountGovan: useFeature(
            '50-off-breach-discount-govan50',
        ),
        canShowBreachDiscountCovid: useFeature(
            '50-off-breach-discount-coviddig',
        ),
        canShowBlackFriday: useFeature('black-friday'),
        canShowPrintDig: useFeature('50-off-breach-discount-printdig'),
    })
}

export function useBreachWallContentType(
    location: H.Location,
    options: BreachWallOptions,
): {
    deal: string
    creativeName: string
    type: BreachWallContentType
} {
    if (options.canShowBlackFriday) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BLKFRI8',
            type: 'black-friday',
        }
    }

    if (showBreachDiscount(location, options.canShowBreachDiscountGovan)) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'GOVAN50',
            type: '50-off-breach-discount-govan50',
        }
    }

    if (options.canShowBreachDiscountCovid) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'COVIDDIG',
            type: '50-off-breach-discount-coviddig',
        }
    }

    if (options.canShowPrintDig) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BRE1DISC',
            type: '50-off-breach-discount-printdig',
        }
    }

    if (showSportBreach(location, options.canShowSportBreach)) {
        return {
            creativeName: 'TW_Article_Breach_Article_SPORT_PACKAGE',
            deal: 'sportspack',
            type: 'sport',
        }
    }
    return {
        creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
        deal: 'default',
        type: 'default',
    }
}

export const BreachNotificationWrapper: React.FC<BreachNotificationWrapperProps> = ({
    breachDeal,
    articleId,
    breachCreativeName,
    onEvent,
    type,
    contentOptions,
}) => {
    const showLiveSportTrailer =
        contentOptions && contentOptions['showLiveSportTrailer']

    switch (type) {
        case 'sport':
            return (
                <SportBreachNotification
                    breachDeal={breachDeal}
                    articleId={articleId}
                    breachCreativeName={breachCreativeName}
                    onEvent={onEvent}
                />
            )
        case 'black-friday':
            return (
                <BlackFridayBreachNotification
                    breachDeal={breachDeal}
                    articleId={articleId}
                    breachCreativeName={breachCreativeName}
                    onEvent={onEvent}
                />
            )
        default:
            return (
                <BreachNotification
                    breachDeal={breachDeal}
                    articleId={articleId}
                    breachCreativeName={breachCreativeName}
                    onEvent={onEvent}
                    type={type}
                    contentOptions={{
                        showLiveSportTrailer,
                    }}
                />
            )
    }
}

BreachNotificationWrapper.displayName = 'BreachNotificationWrapper'

export default BreachNotificationWrapper
